/**
 * lib/testimonial-trim.ts — website copy (2026-09-25) of pf-platform
 * lib/testimonials/trim.ts, used by components/video/TestimonialPlayer.tsx.
 *
 * trim — a video testimonial's start and end times (Round 6, plan item 7).
 *
 * Non-destructive: the uploaded video is never cut. The testimonial stores
 * trim_in / trim_out (seconds, NULL = the video's own start / end) and the
 * person's markers (seconds). Our players play only [in, out].
 *
 *   snapToMarker   the "magnet": a dragged handle lands exactly on a marker
 *                  once it is within `threshold` seconds of it.
 *   normalizeTrim  what is stored: in < out, inside the video, a minimum
 *                  length, and NULL where a point is the video's own edge.
 *   parseTrimInput the submit route's validation of what the browser sent.
 *   clipWindow     the player's [start, end] for a given trim + duration.
 */

/** Shortest clip we store (seconds). Anything shorter is treated as "no trim". */
export const MIN_CLIP_SECONDS = 1;
/** Most markers kept per testimonial. */
export const MAX_MARKERS = 20;
/** Longest video (seconds) the numbers may refer to: the recorder's 2 minutes, with room for a picked file. */
const MAX_SECONDS = 60 * 60;

/** Round to milliseconds, so stored values compare exactly. */
export const ms = (s: number) => Math.round(s * 1000) / 1000;

/**
 * The magnet. Returns the nearest marker when it is within `threshold`
 * (inclusive) of `position`, else `position` unchanged. Ties go to the
 * earlier marker. `snapped` says whether the magnet took it (for the
 * haptic/visual "click").
 */
export function snapToMarker(
  position: number,
  markers: readonly number[],
  threshold: number
): { position: number; snapped: boolean; marker: number | null } {
  if (!Number.isFinite(position)) return { position, snapped: false, marker: null };
  let best: number | null = null;
  let bestDist = Infinity;
  for (const m of markers) {
    if (!Number.isFinite(m)) continue;
    const d = Math.abs(m - position);
    if (d < bestDist || (d === bestDist && best !== null && m < best)) {
      best = m;
      bestDist = d;
    }
  }
  if (best !== null && threshold >= 0 && bestDist <= threshold) {
    return { position: best, snapped: true, marker: best };
  }
  return { position, snapped: false, marker: null };
}

/** Sorted, de-duplicated (to the millisecond), finite, inside [0, duration] when the duration is known. */
export function cleanMarkers(markers: readonly unknown[], duration?: number | null): number[] {
  const max = duration && Number.isFinite(duration) && duration > 0 ? duration : MAX_SECONDS;
  const out = new Set<number>();
  for (const m of markers) {
    if (typeof m !== "number" || !Number.isFinite(m) || m < 0 || m > max) continue;
    out.add(ms(m));
  }
  return [...out].sort((a, b) => a - b).slice(0, MAX_MARKERS);
}

/**
 * What is stored for a trim. `null` = that end is the video's own edge.
 * An in-point at (or within a millisecond of) 0 → null; an out-point at the
 * end → null. Returns both null when the result would be shorter than
 * MIN_CLIP_SECONDS or inverted.
 */
export function normalizeTrim(
  trimIn: number | null | undefined,
  trimOut: number | null | undefined,
  duration?: number | null
): { trimIn: number | null; trimOut: number | null } {
  const dur = duration && Number.isFinite(duration) && duration > 0 ? duration : null;
  let a = typeof trimIn === "number" && Number.isFinite(trimIn) ? Math.max(0, trimIn) : 0;
  let b = typeof trimOut === "number" && Number.isFinite(trimOut) ? trimOut : dur ?? Infinity;
  if (dur !== null) {
    a = Math.min(a, dur);
    b = Math.min(b, dur);
  }
  if (!(b - a >= MIN_CLIP_SECONDS)) return { trimIn: null, trimOut: null };
  const inOut = a < 0.001 ? null : ms(a);
  const outOut = !Number.isFinite(b) || (dur !== null && dur - b < 0.001) ? null : ms(b);
  return { trimIn: inOut, trimOut: outOut };
}

function num(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * The submit route's reading of the browser's trim fields. Never throws;
 * anything unreadable becomes "no trim" / "no markers" (the testimonial
 * itself is never refused over a trim).
 */
export function parseTrimInput(input: { trimIn?: unknown; trimOut?: unknown; markers?: unknown }): {
  trimIn: number | null;
  trimOut: number | null;
  markers: number[] | null;
} {
  const a = num(input.trimIn);
  const b = num(input.trimOut);
  const inRange = (x: number | null) => (x !== null && x >= 0 && x <= MAX_SECONDS ? x : null);
  const { trimIn, trimOut } = a === null && b === null ? { trimIn: null, trimOut: null } : normalizeTrim(inRange(a), inRange(b));
  let markers: number[] | null = null;
  let raw: unknown = input.markers;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  if (Array.isArray(raw)) {
    const m = cleanMarkers(raw);
    markers = m.length > 0 ? m : null;
  }
  return { trimIn, trimOut, markers };
}

/** The part a player plays: [start, end]; `end` is null while the duration is unknown and no out-point is set. */
export function clipWindow(
  trim: { trimIn?: number | null; trimOut?: number | null } | null | undefined,
  duration: number
): { start: number; end: number | null } {
  const dur = Number.isFinite(duration) && duration > 0 ? duration : null;
  const start = Math.max(0, trim?.trimIn ?? 0);
  let end = trim?.trimOut ?? dur;
  if (end !== null && dur !== null) end = Math.min(end, dur);
  if (end !== null && end - start < MIN_CLIP_SECONDS) return { start: 0, end: dur };
  return { start: dur !== null ? Math.min(start, dur) : start, end };
}
